import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Zap, Activity, TrendingUp, TrendingDown, RefreshCw, Calendar, ArrowRight, Atom, Plug, Gauge, AlertOctagon } from 'lucide-react';
// --- Mock Data ---
const marketData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  const nuclear = 40 + Math.random() * 2; // Stable nuclear base
  const demand = 50 + (hour >= 8 && hour <= 20 ? 15 : 0) + Math.sin(hour / 4) * 5;
  const export_ = Math.max(0, nuclear - demand + 20);
  const price = 60 + demand / 70 * 30 + Math.random() * 5;
  return {
    time: `${hour}:00`,
    nuclear: parseFloat(nuclear.toFixed(1)),
    demand: parseFloat(demand.toFixed(1)),
    export: parseFloat(export_.toFixed(1)),
    price: Math.round(price),
    capacity: 85 // Total capacity %
  };
});
const generationMix = [{
  name: 'Nuclear',
  value: 68,
  color: '#6366f1'
}, {
  name: 'Hydro',
  value: 12,
  color: '#0ea5e9'
}, {
  name: 'Wind',
  value: 8,
  color: '#06b6d4'
}, {
  name: 'Gas',
  value: 6,
  color: '#f97316'
}, {
  name: 'Solar',
  value: 4,
  color: '#eab308'
}, {
  name: 'Other',
  value: 2,
  color: '#64748b'
}];
const interconnectors = [{
  country: 'UK',
  flow: 2.1,
  type: 'export',
  status: 'active'
}, {
  country: 'Germany',
  flow: -1.5,
  type: 'import',
  status: 'active'
}, {
  country: 'Spain',
  flow: 1.8,
  type: 'export',
  status: 'active'
}, {
  country: 'Italy',
  flow: 2.4,
  type: 'export',
  status: 'active'
}, {
  country: 'Switzerland',
  flow: 0.9,
  type: 'export',
  status: 'maintenance'
}];
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-indigo-100 rounded-xl p-4 shadow-xl z-50" data-id="element-1881">
        <p className="text-xs font-bold text-slate-900 mb-2" data-id="element-1882">{label}</p>
        <div className="space-y-1" data-id="element-1883">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-1884">
              <div className="flex items-center gap-2" data-id="element-1885">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-1886" />
                <span className="text-xs text-slate-600 capitalize" data-id="element-1887">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-slate-900" data-id="element-1888">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function FranceMarketDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-indigo-50/30" data-id="element-1889">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1890">
        <div data-id="element-1891">
          <div className="flex items-center gap-2 mb-1" data-id="element-1892">
            <div className="p-2 bg-indigo-600 rounded-lg" data-id="element-1893">
              <Atom size={20} className="text-white" data-id="element-1894" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-1895">
              France Power Market
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-1896">
            Nuclear generation, cross-border flows, and baseload stability
          </p>
        </div>

        <div className="flex items-center gap-3 ml-11 md:ml-0" data-id="element-1897">
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-1898">
            <Calendar size={16} data-id="element-1899" />
            <span data-id="element-1900">Today, Oct 24</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-500/20" data-id="element-1901">
            <RefreshCw size={16} data-id="element-1902" />
            <span data-id="element-1903">Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1904">
        {[{
        title: 'Nuclear Output',
        value: '42.5',
        unit: 'GW',
        change: '+1.2%',
        trend: 'up',
        icon: Atom,
        color: 'indigo'
      }, {
        title: 'Net Export',
        value: '5.7',
        unit: 'GW',
        change: '+8.4%',
        trend: 'up',
        icon: ArrowRight,
        color: 'blue'
      }, {
        title: 'Baseload Price',
        value: '€68.50',
        unit: '/MWh',
        change: '-2.1%',
        trend: 'down',
        icon: Zap,
        color: 'emerald'
      }, {
        title: 'Grid Load',
        value: '58.2',
        unit: 'GW',
        change: '+3.4%',
        trend: 'up',
        icon: Activity,
        color: 'violet'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group" data-id="element-1905">
            <div className="flex justify-between items-start mb-4" data-id="element-1906">
              <div className={`p-2 rounded-lg bg-${metric.color}-50 text-${metric.color}-600 group-hover:bg-${metric.color}-100 transition-colors`} data-id="element-1907">
                <metric.icon size={20} data-id="element-1908" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : metric.trend === 'down' ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-700'}`} data-id="element-1909">
                {metric.trend === 'up' ? <TrendingUp size={12} data-id="element-1910" /> : <TrendingDown size={12} data-id="element-1911" />}
                {metric.change}
              </div>
            </div>
            <div data-id="element-1912">
              <p className="text-sm text-slate-500 font-medium" data-id="element-1913">
                {metric.title}
              </p>
              <div className="flex items-baseline gap-1 mt-1" data-id="element-1914">
                <span className="text-2xl font-bold text-slate-900" data-id="element-1915">
                  {metric.value}
                </span>
                <span className="text-sm text-slate-400 font-medium" data-id="element-1916">
                  {metric.unit}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1917">
        {/* Generation Stack */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1918">
          <div className="flex items-center justify-between mb-6" data-id="element-1919">
            <div data-id="element-1920">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-1921">
                Wind Speed Forecast - France
              </h3>
              <p className="text-sm text-slate-400" data-id="element-1922">
                High-resolution wind model (ECMWF)
              </p>
            </div>
            <div className="flex gap-2" data-id="element-1923">
              <span className="px-2 py-1 bg-indigo-50 rounded text-xs font-medium text-indigo-700 border border-indigo-100" data-id="element-1924">
                Nuclear Availability: 82%
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-1925">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1926">
              <AreaChart data={marketData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-1927">
                <defs data-id="element-1928">
                  <linearGradient id="colorNuclear" x1="0" y1="0" x2="0" y2="1" data-id="element-1929">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} data-id="element-1930" />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} data-id="element-1931" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-1932" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} dy={10} data-id="element-1933" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} label={{
                value: 'Power (GW)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#64748b',
                  fontSize: 12
                }
              }} data-id="element-1934" />
                <Tooltip content={<CustomTooltip data-id="element-1936" />} data-id="element-1935" />
                <Legend iconType="circle" wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-1937" />
                <Area type="monotone" dataKey="nuclear" name="Nuclear Gen" stroke="#6366f1" fill="url(#colorNuclear)" strokeWidth={2} data-id="element-1938" />
                <Area type="monotone" dataKey="demand" name="Total Demand" stroke="#0f172a" fill="none" strokeWidth={2} data-id="element-1939" />
                <Area type="monotone" dataKey="export" name="Net Export" stroke="#0ea5e9" fill="none" strokeWidth={2} strokeDasharray="5 5" data-id="element-1940" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Generation Mix Pie */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-1941">
          <div className="mb-6" data-id="element-1942">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1943">Energy Mix</h3>
            <p className="text-sm text-slate-500" data-id="element-1944">Current generation sources</p>
          </div>

          <div className="flex-1 min-h-[300px]" data-id="element-1945">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1946">
              <PieChart data-id="element-1947">
                <Pie data={generationMix} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1948">
                  {generationMix.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1949" />)}
                </Pie>
                <Tooltip content={<CustomTooltip data-id="element-1951" />} data-id="element-1950" />
                <Legend layout="vertical" verticalAlign="middle" align="right" iconType="circle" data-id="element-1952" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1953">
        {/* Interconnector Flows */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1954">
          <div className="mb-6" data-id="element-1955">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1956">
              Interconnector Flows
            </h3>
            <p className="text-sm text-slate-500" data-id="element-1957">Cross-border exchange (GW)</p>
          </div>

          <div className="space-y-4" data-id="element-1958">
            {interconnectors.map(conn => <div key={conn.country} className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-slate-100" data-id="element-1959">
                <div className="flex items-center gap-3" data-id="element-1960">
                  <div className={`p-1.5 rounded-full ${conn.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-1961">
                    <Plug size={16} data-id="element-1962" />
                  </div>
                  <div data-id="element-1963">
                    <p className="font-bold text-slate-900" data-id="element-1964">{conn.country}</p>
                    <p className="text-xs text-slate-500 capitalize" data-id="element-1965">
                      {conn.status}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1966">
                  <p className={`font-bold ${conn.flow > 0 ? 'text-blue-600' : 'text-indigo-600'}`} data-id="element-1967">
                    {conn.flow > 0 ? 'Export' : 'Import'} {Math.abs(conn.flow)}{' '}
                    GW
                  </p>
                  <div className="w-24 h-1.5 bg-slate-200 rounded-full mt-1 ml-auto overflow-hidden" data-id="element-1968">
                    <div className={`h-full rounded-full ${conn.flow > 0 ? 'bg-blue-500' : 'bg-indigo-500'}`} style={{
                  width: `${Math.abs(conn.flow) / 3 * 100}%`
                }} data-id="element-1969" />
                  </div>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Nuclear Availability */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1970">
          <div className="mb-6" data-id="element-1971">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1972">
              Nuclear Fleet Status
            </h3>
            <p className="text-sm text-slate-500" data-id="element-1973">
              Reactor availability & maintenance
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1974">
            <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 text-center" data-id="element-1975">
              <p className="text-xs text-indigo-600 font-medium uppercase" data-id="element-1976">
                Online
              </p>
              <p className="text-2xl font-bold text-indigo-900" data-id="element-1977">42/56</p>
              <p className="text-xs text-indigo-400" data-id="element-1978">Reactors</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 text-center" data-id="element-1979">
              <p className="text-xs text-amber-600 font-medium uppercase" data-id="element-1980">
                Maintenance
              </p>
              <p className="text-2xl font-bold text-amber-900" data-id="element-1981">14</p>
              <p className="text-xs text-amber-400" data-id="element-1982">Reactors</p>
            </div>
          </div>

          <div className="h-[150px] w-full" data-id="element-1983">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1984">
              <BarChart data={marketData.filter((_, i) => i % 4 === 0)} layout="vertical" data-id="element-1985">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" data-id="element-1986" />
                <XAxis type="number" hide data-id="element-1987" />
                <YAxis dataKey="time" type="category" width={40} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} axisLine={false} tickLine={false} data-id="element-1988" />
                <Tooltip cursor={{
                fill: 'transparent'
              }} content={<CustomTooltip data-id="element-1990" />} data-id="element-1989" />
                <Bar dataKey="capacity" name="Availability %" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={12} data-id="element-1991" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>;
}